import ti18n from "i18n!nls/TranscriptionQuotaErrori18n";
import PersistentModels from "services/PersistentModels";
import ExpectedError, {
    EXPECTED_ERROR_CATEGORY,
    EXPECTED_ERROR_CLASSIFICATION,
} from "models/helper/ExpectedError";

// Single source of truth for detecting and presenting "out of transcription
// minutes" across every transcription entry point (record/upload flow and the
// sequencer transcribe-&-replace flow).
//
// Workspace-specific messaging ("ask your workspace owner") is intentionally NOT
// handled here. It is owned by the upstream controller pre-check, which uses
// fresh server-side workspace usage data to short-circuit before transcription
// even starts. There is no stale-proof client-side way to tell a workspace 402
// from an individual one (the backend uses the same errorCode for both, and all
// cached workspace state can be stale), so this helper always offers the upgrade
// CTA rather than risk hiding it from an individual user.

const QUOTA_ERROR_CODES = ["workspace/transcription_quota_exceeded", "quota_exceeded"];
const QUOTA_ERROR_MESSAGES = ["Transcription quota exceeded for this billing cycle"];

function _numberOrNull(value) {
    return (typeof value === "number" && !isNaN(value)) ? value : null;
}

// Read a field off either the top-level object or its `.response` envelope.
function _coalesceField(errorOrResult, field) {
    if (!errorOrResult || typeof errorOrResult !== "object") {
        return undefined;
    }
    if (errorOrResult[field] !== undefined && errorOrResult[field] !== null) {
        return errorOrResult[field];
    }
    if (errorOrResult.response && errorOrResult.response[field] !== undefined && errorOrResult.response[field] !== null) {
        return errorOrResult.response[field];
    }
    return undefined;
}

function _extractQuotaResponse(errorOrResult) {
    if (errorOrResult && errorOrResult.response && typeof errorOrResult.response === "object") {
        return errorOrResult.response;
    }
    return (errorOrResult && typeof errorOrResult === "object") ? errorOrResult : {};
}

function isTranscriptionQuotaError(errorOrResult) {
    if (!errorOrResult || typeof errorOrResult !== "object") {
        return false;
    }
    if (errorOrResult.isTranscriptionQuotaError === true) {
        return true;
    }
    const errorCode = _coalesceField(errorOrResult, "errorCode");
    if (errorCode && QUOTA_ERROR_CODES.indexOf(errorCode) !== -1) {
        return true;
    }
    const errorMessage = _coalesceField(errorOrResult, "errorMessage") || _coalesceField(errorOrResult, "message");
    if (errorMessage && QUOTA_ERROR_MESSAGES.indexOf(errorMessage) !== -1) {
        return true;
    }
    return _coalesceField(errorOrResult, "userDoesNotHaveEnoughTranscriptionMinutes") === true;
}

// Base plan minutes + purchased top-ups. The backend snapshot splits these
// (transcriptionMinutesPerMonth excludes extraMinutesPurchased), so summing them
// gives the user's real total allowance.
function _computeTotalMinutes(response) {
    const perMonth = _numberOrNull(response && response.transcriptionMinutesPerMonth) || 0;
    const extra = _numberOrNull(response && response.extraMinutesPurchased) || 0;
    return perMonth + extra;
}

function _buildMessage(response) {
    const used = _numberOrNull(response && response.transcriptionMinutesUsedThisMonth);
    const total = _computeTotalMinutes(response);
    if (used !== null && total > 0) {
        return window.getI18n(ti18n, "OUT_OF_TRANSCRIPTION_MINUTES_WITH_COUNT")
            .replace("{used}", String(used))
            .replace("{total}", String(total));
    }
    return window.getI18n(ti18n, "OUT_OF_TRANSCRIPTION_MINUTES_GENERIC");
}

// Turn any quota wire format (new 402 error or legacy 200+flag operationResult)
// into one stable Error instance with a populated `.message` (so downstream
// sanitizers never store "[object Object]"), a detection marker, the errorCode,
// and the structured quota numbers.
function normalizeTranscriptionQuotaError(errorOrResult) {
    const response = _extractQuotaResponse(errorOrResult);
    const errorCode = _coalesceField(errorOrResult, "errorCode") || "quota_exceeded";
    const backendMessage =
        _coalesceField(errorOrResult, "errorMessage") ||
        _coalesceField(errorOrResult, "message") ||
        null;
    const quotaError = new ExpectedError(
        backendMessage || _buildMessage(response),
        {
            i18nKey: "TRANSCRIPTION_QUOTA_EXCEEDED",
            classification: EXPECTED_ERROR_CLASSIFICATION.BUSINESS,
            errorCode: errorCode,
            expectedErrorCategory: EXPECTED_ERROR_CATEGORY.TRANSCRIPTION_QUOTA_EXCEEDED,
        }
    );
    quotaError.isTranscriptionQuotaError = true;
    quotaError.errorCode = errorCode;
    quotaError.userDoesNotHaveEnoughTranscriptionMinutes = true;
    quotaError.response = {
        transcriptionMinutesPerMonth: _numberOrNull(response.transcriptionMinutesPerMonth),
        transcriptionMinutesUsedThisMonth: _numberOrNull(response.transcriptionMinutesUsedThisMonth),
        extraMinutesPurchased: _numberOrNull(response.extraMinutesPurchased)
    };
    return quotaError;
}

function showOutOfTranscriptionMinutesPrompt(errorOrResult, options) {
    const response = _extractQuotaResponse(errorOrResult);
    const router = PersistentModels.getInstance().getRouter();
    // Callers pass `upgradeReturnUrls` for the context they came from (e.g. the
    // record/create flow returns to the create page). When omitted we pass
    // undefined through so the upgrade controller uses its own neutral default
    // (the subscription-completed page) instead of dropping the user on the
    // create page after they upgrade while editing an existing project.
    const upgradeReturnUrls = (options && options.upgradeReturnUrls) || null;
    const onUserClickedOnUpgradeNowButton = function() {
        return router.showUpgradeCreatorSubscriptionPopup(
            upgradeReturnUrls ? upgradeReturnUrls.onUpgradeCompleted : undefined,
            upgradeReturnUrls ? upgradeReturnUrls.onPaymentMethodUpdated : undefined,
            true
        );
    };
    window.alertUserErrorMessage(
        _buildMessage(response),
        null,
        onUserClickedOnUpgradeNowButton,
        window.getI18n(ti18n, "UPGRADE_NOW")
    );
}

export { isTranscriptionQuotaError, normalizeTranscriptionQuotaError, showOutOfTranscriptionMinutesPrompt };
export default { isTranscriptionQuotaError, normalizeTranscriptionQuotaError, showOutOfTranscriptionMinutesPrompt };
